import { useMemo } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTranslation } from 'react-i18next'
import {
  getIso7010PictogramUrl,
  ISO_7010_PICTOGRAM_CODES,
} from './iso7010Pictograms'

const PICTOGRAM_NONE_VALUE = '__none__'

function getPictogramLabel(code: string, translate: (key: string) => string): string {
  const name = translate(`${code}.name`)
  return name !== `${code}.name` ? name : code
}

export type PictogramSelectProps = {
  value: string | null
  onChange: (value: string | null) => void
  disabled?: boolean
  id?: string
  placeholder?: string
}

export function PictogramSelect({
  value,
  onChange,
  disabled = false,
  id,
  placeholder = 'None',
}: PictogramSelectProps) {
  const { t } = useTranslation('pictograms')
  const selectValue = value && value.trim() !== '' ? value : PICTOGRAM_NONE_VALUE

  const handleValueChange = (newValue: string) => {
    if (newValue === PICTOGRAM_NONE_VALUE) {
      onChange(null)
    } else {
      onChange(newValue)
    }
  }

  const pictogramItems = useMemo(() => {
    const items = [{ value: PICTOGRAM_NONE_VALUE, label: placeholder }]
    for (const code of ISO_7010_PICTOGRAM_CODES) {
      items.push({ value: code, label: getPictogramLabel(code, t) })
    }
    return items
  }, [placeholder, t])

  return (
    <Select
      value={selectValue}
      onValueChange={(value) => {
        if (value) {
          handleValueChange(value)
        }
      }}
      disabled={disabled}
      items={pictogramItems}
    >
      <SelectTrigger id={id} className='w-full'>
        <SelectValue placeholder={placeholder}>
          {value && value.trim() !== '' ? (
            <span className='flex items-center gap-2'>
              <img
                src={getIso7010PictogramUrl(value)}
                alt=""
                className='size-6 object-contain'
              />
              {getPictogramLabel(value, t)}
            </span>
          ) : null}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={PICTOGRAM_NONE_VALUE}>{placeholder}</SelectItem>
        {ISO_7010_PICTOGRAM_CODES.map((code) => (
          <SelectItem key={code} value={code}>
            <span className='flex items-center gap-2'>
              <img
                src={getIso7010PictogramUrl(code)}
                alt=""
                className='size-6 object-contain'
              />
              {getPictogramLabel(code, t)}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
